import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import { KeyTerm, ImportantNote, Mnemonic } from "../note-components";
import type { UnitNotes } from "../types";

const biologyUnit4Notes: UnitNotes = {
  unitId: "unit-4",
  unitName: "Unit 4: Cell Communication and Cell Cycle",
  examWeight: "10–15%",
  focus: "How cells detect and respond to environmental signals, and the regulated process of cell division.",
  sections: [
    {
      id: "topic-4-1",
      title: "Topic 4.1: Cell Communication",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🧬 Evolutionary Origins of Signaling</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Signal transduction pathways are highly conserved across the tree of life, suggesting evolution from a common ancestor.
            </p>
            <div className="bg-gradient-to-r from-emerald-50 to-teal-50 p-4 rounded-lg border border-emerald-200 mb-4">
              <h4 className="font-bold text-emerald-900">🧫 Quorum Sensing in Bacteria</h4>
              <p className="text-sm text-slate-700 mt-1">
                Bacteria secrete <strong>autoinducers</strong> to detect population density. At specific thresholds, they launch coordinated responses like biofilm formation or toxin release.
              </p>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">📡 Types of Cell Signaling</h3>
            <div className="space-y-4">
              <div className="bg-white p-4 rounded-lg border border-slate-200 shadow-sm">
                <h4 className="font-bold text-indigo-900 mb-3">🤝 A. Direct Contact</h4>
                <div className="grid md:grid-cols-3 gap-3">
                  <div className="p-3 bg-indigo-50 rounded border border-indigo-100">
                    <p className="font-semibold text-xs text-indigo-800">Plasmodesmata (Plants)</p>
                    <p className="text-xs text-slate-600">Channels between cell walls allowing free passage of cytosol/ions.</p>
                  </div>
                  <div className="p-3 bg-indigo-50 rounded border border-indigo-100">
                    <p className="font-semibold text-xs text-indigo-800">Gap Junctions (Animals)</p>
                    <p className="text-xs text-slate-600">Protein channels (connexins) vital for cardiac muscle contraction.</p>
                  </div>
                  <div className="p-3 bg-indigo-50 rounded border border-indigo-100">
                    <p className="font-semibold text-xs text-indigo-800">Cell-Cell Recognition</p>
                    <p className="text-xs text-slate-600">Surface glycoproteins/lipids interacting (e.g., APCs and T-Cells).</p>
                  </div>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-gradient-to-br from-blue-50 to-sky-50 p-4 rounded-lg border border-blue-200">
                  <h4 className="font-bold text-blue-900">📍 B. Local Signaling</h4>
                  <ul className="list-disc pl-5 mt-2 space-y-2 text-sm text-slate-700">
                    <li><strong>Paracrine:</strong> Acts on nearby cells (e.g., <em>growth factors</em> or <em>morphogens</em> in embryos).</li>
                    <li><strong>Synaptic:</strong> Electric signals trigger <em>neurotransmitters</em> across a synapse.</li>
                  </ul>
                </div>
                <div className="bg-gradient-to-br from-purple-50 to-fuchsia-50 p-4 rounded-lg border border-purple-200">
                  <h4 className="font-bold text-purple-900">🌎 C. Long Distance Signaling</h4>
                  <p className="text-sm text-slate-700 mt-2">
                    <strong>Endocrine Signaling:</strong> Hormones travel via the circulatory system (bloodstream). Only cells with specific receptors respond.
                  </p>
                  <p className="text-xs text-slate-500 mt-2 italic">Example: Insulin released by the pancreas reaching liver/muscles.</p>
                </div>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-4-2",
      title: "Topic 4.2: Introduction to Signal Transduction",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🔄 The Three Stages of Cell Signaling</h3>
            <p className="text-sm text-slate-600 mb-4">Model established by Earl Sutherland.</p>
            <div className="grid md:grid-cols-3 gap-4">
              <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
                <h4 className="font-bold text-amber-900">1. Reception</h4>
                <p className="text-xs text-slate-700 mt-1">Detection of a <strong>ligand</strong>. Binding is highly specific and causes a <strong>conformational change</strong>.</p>
              </div>
              <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                <h4 className="font-bold text-orange-900">2. Transduction</h4>
                <p className="text-xs text-slate-700 mt-1">Conversion of the signal into a form that can bring about a response.</p>
              </div>
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <h4 className="font-bold text-red-900">3. Response</h4>
                <p className="text-xs text-slate-700 mt-1">The triggered specific cellular activity.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🔑 Specific Receptors</h3>
            <div className="space-y-4">
              <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                <h4 className="font-bold text-slate-900 mb-2">A. Membrane Receptors (Hydrophilic Ligands)</h4>
                <div className="grid md:grid-cols-2 gap-4">
                  <KeyTerm 
                    term="GPCRs" 
                    definition="G-Protein Coupled Receptors. Ligand binding swaps GDP for GTP to activate a G-protein." 
                  />
                  <KeyTerm 
                    term="Ion Channels" 
                    definition="Ligand-gated 'gates' allowing ions like Na⁺ or Ca²⁺ to flow through." 
                  />
                </div>
              </div>
              <div className="bg-gradient-to-r from-teal-50 to-emerald-50 p-4 rounded-lg border border-teal-200">
                <h4 className="font-bold text-teal-900">B. Intracellular Receptors (Hydrophobic Ligands)</h4>
                <p className="text-sm text-slate-700 mt-1">
                  Receptors for nonpolar ligands (Steroids like <strong>Testosterone/Estrogen</strong>; Nitric Oxide) in cytoplasm or nucleus. Often act as <strong>Transcription Factors</strong>.
                </p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-4-3",
      title: "Topic 4.3: Signal Transduction Pathways",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">⚡ Relaying the Signal</h3>
            <div className="grid md:grid-cols-2 gap-4 mb-4">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-900">Signal Amplification</h4>
                <p className="text-sm text-slate-700 mt-1">A small number of ligands triggers a massive response. One enzyme activates many molecules in the next step.</p>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-900">Phosphorylation Cascade</h4>
                <ul className="list-disc pl-5 mt-1 space-y-1 text-xs text-slate-700">
                  <li><strong>Kinases:</strong> Add phosphate groups from ATP (activate).</li>
                  <li><strong>Phosphatases:</strong> Remove phosphate groups (deactivate/reset).</li>
                </ul>
              </div>
            </div>
            <div className="bg-white p-4 rounded-lg border border-slate-200">
              <h4 className="font-bold text-slate-800 mb-2">Second Messengers</h4>
              <p className="text-sm text-slate-700 mb-3">Small, non-protein, water-soluble molecules that spread via diffusion.</p>
              <div className="grid md:grid-cols-2 gap-3">
                <div className="p-3 bg-slate-50 rounded border">
                  <p className="font-bold text-xs">Cyclic AMP (cAMP)</p>
                  <p className="text-xs text-slate-600">ATP converted by <em>Adenylyl Cyclase</em>; activates Protein Kinase A.</p>
                </div>
                <div className="p-3 bg-slate-50 rounded border">
                  <p className="font-bold text-xs">Calcium Ions (<InlineMath math="Ca^{2+}" />)</p>
                  <p className="text-xs text-slate-600">Stored in ER; signal triggers influx to activate proteins.</p>
                </div>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🛠️ Regulation and Interference</h3>
            <div className="space-y-3">
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <h4 className="font-bold text-red-900">Pathways and Mutations</h4>
                <p className="text-sm text-slate-700 mt-1">Mutations can result in pathways being permanently "on" (cancer) or "off."</p>
                <p className="text-xs text-slate-500 mt-1">Example: <strong>Anthrax toxin</strong> destroys adenylyl cyclase, blocking signal transduction.</p>
              </div>
              <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                <h4 className="font-bold text-slate-900">Termination</h4>
                <p className="text-xs text-slate-700">Ligand detachment, G-protein hydrolysis (GTP $\rightarrow$ GDP), or Phosphodiesterase converting cAMP $\rightarrow$ AMP.</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-4-4",
      title: "Topic 4.4: Feedback",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">⚖️ Negative Feedback (Homeostasis)</h3>
            <p className="text-sm text-slate-700 mb-4">Mechanisms that return a system to a set point. The response <strong>counteracts</strong> the stimulus.</p>
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <h4 className="font-bold text-blue-900 mb-2">Example: Blood Sugar Regulation</h4>
              <ul className="list-disc pl-5 space-y-2 text-sm text-slate-700">
                <li><strong>High Blood Glucose:</strong> Pancreas releases <strong>Insulin</strong> $\rightarrow$ Cells take up glucose $\rightarrow$ Glucose drops.</li>
                <li><strong>Low Blood Glucose:</strong> Pancreas releases <strong>Glucagon</strong> $\rightarrow$ Liver releases stored glucose $\rightarrow$ Glucose rises.</li>
              </ul>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">📢 Positive Feedback</h3>
            <p className="text-sm text-slate-700 mb-4">Mechanisms that amplify the response. Moves the system <em>away</em> from the set point to drive a process to completion.</p>
            <div className="grid md:grid-cols-3 gap-3">
              <div className="bg-rose-50 p-3 rounded-lg border border-rose-200">
                <h5 className="font-bold text-rose-900 text-xs">👶 Childbirth</h5>
                <p className="text-xs text-slate-600 mt-1">Pressure on cervix triggers <strong>Oxytocin</strong> $\rightarrow$ harder contractions $\rightarrow$ more pressure.</p>
              </div>
              <div className="bg-rose-50 p-3 rounded-lg border border-rose-200">
                <h5 className="font-bold text-rose-900 text-xs">🩸 Blood Clotting</h5>
                <p className="text-xs text-slate-600 mt-1">Platelets release chemicals to attract even more platelets.</p>
              </div>
              <div className="bg-rose-50 p-3 rounded-lg border border-rose-200">
                <h5 className="font-bold text-rose-900 text-xs">🍎 Fruit Ripening</h5>
                <p className="text-xs text-slate-600 mt-1">Ripe fruit releases <strong>Ethylene</strong> gas, triggering nearby fruit to ripen.</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-4-5",
      title: "Topic 4.5: Cell Cycle",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">📦 Organization of DNA</h3>
            <div className="grid md:grid-cols-2 gap-3 mb-4">
              <KeyTerm term="Chromatin" definition="Uncoiled DNA + Histones (during Interphase)." />
              <KeyTerm term="Chromosomes" definition="Tightly coiled DNA (during division)." />
              <KeyTerm term="Sister Chromatids" definition="Identical copies of a chromosome attached at the centromere (created in S-Phase)." />
              <KeyTerm term="Genome" definition="A cell's total genetic information." />
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">⏳ Interphase (90% of Cycle)</h3>
            <div className="space-y-2">
              <div className="bg-slate-50 p-3 rounded border border-slate-200 flex items-center gap-4">
                <span className="font-bold text-slate-800 w-8"><InlineMath math="G_1" /></span>
                <p className="text-sm text-slate-700">Cell grows and produces proteins/organelles. <strong><InlineMath math="G_0" /></strong> is the non-dividing exit state.</p>
              </div>
              <div className="bg-emerald-50 p-3 rounded border border-emerald-200 flex items-center gap-4">
                <span className="font-bold text-emerald-800 w-8"><InlineMath math="S" /></span>
                <p className="text-sm text-slate-700"><strong>Synthesis:</strong> DNA Replication occurs.</p>
              </div>
              <div className="bg-slate-50 p-3 rounded border border-slate-200 flex items-center gap-4">
                <span className="font-bold text-slate-800 w-8"><InlineMath math="G_2" /></span>
                <p className="text-sm text-slate-700">Growth continues; production of microtubules for division.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">✨ M-Phase (Mitosis & Cytokinesis)</h3>
            <Mnemonic title="PMAT" content="Prophase, Metaphase, Anaphase, Telophase." />
            <div className="mt-4 space-y-3">
              <div className="p-3 bg-white border-l-4 border-indigo-400 shadow-sm">
                <p className="text-sm"><strong>Prophase:</strong> Chromatin condenses; spindle forms; nucleoli disappear.</p>
              </div>
              <div className="p-3 bg-white border-l-4 border-indigo-400 shadow-sm">
                <p className="text-sm"><strong>Prometaphase:</strong> Nuclear envelope fragments; microtubules attach to <strong>kinetochores</strong>.</p>
              </div>
              <div className="p-3 bg-white border-l-4 border-indigo-400 shadow-sm">
                <p className="text-sm"><strong>Metaphase:</strong> Chromosomes line up at the <strong>metaphase plate</strong> (equator).</p>
              </div>
              <div className="p-3 bg-white border-l-4 border-indigo-400 shadow-sm">
                <p className="text-sm"><strong>Anaphase:</strong> Sister chromatids separate and move to opposite poles.</p>
              </div>
              <div className="p-3 bg-white border-l-4 border-indigo-400 shadow-sm">
                <p className="text-sm"><strong>Telophase:</strong> Two daughter nuclei form; nuclear envelopes arise.</p>
              </div>
            </div>
            <div className="bg-gradient-to-r from-orange-50 to-amber-50 p-4 rounded-lg border border-orange-200 mt-4">
              <h4 className="font-bold text-orange-900">Cytokinesis</h4>
              <p className="text-sm text-slate-700"><strong>Animals:</strong> Cleavage furrow (actin/myosin). <strong>Plants:</strong> Cell plate (Golgi vesicles).</p>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-4-6",
      title: "Topic 4.6: Regulation of Cell Cycle",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🚦 Molecular Control System</h3>
            <div className="grid md:grid-cols-3 gap-3">
              <div className="bg-purple-50 p-3 rounded border border-purple-200">
                <h5 className="font-bold text-purple-900 text-xs">Cyclins</h5>
                <p className="text-xs text-slate-600">Proteins that fluctuate in concentration throughout the cycle.</p>
              </div>
              <div className="bg-purple-50 p-3 rounded border border-purple-200">
                <h5 className="font-bold text-purple-900 text-xs">Cdks</h5>
                <p className="text-xs text-slate-600">Kinases that are active only when attached to a cyclin.</p>
              </div>
              <div className="bg-purple-50 p-3 rounded border border-purple-200">
                <h5 className="font-bold text-purple-900 text-xs">MPF</h5>
                <p className="text-xs text-slate-600">The Cyclin-Cdk complex triggering passage from <InlineMath math="G_2" /> to M phase.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🛑 Checkpoints</h3>
            <div className="space-y-3">
              <div className="p-3 bg-slate-50 border rounded">
                <p className="text-sm"><strong><InlineMath math="G_1" /> Checkpoint:</strong> The 'Restriction Point'. If failed, cell enters <InlineMath math="G_0" />.</p>
              </div>
              <div className="p-3 bg-slate-50 border rounded">
                <p className="text-sm"><strong>M Checkpoint:</strong> Ensures all chromosomes are attached to spindle fibers to prevent nondisjunction.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">⚠️ Cancer: Regulation Loss</h3>
            <ImportantNote>
              Cancer cells evade <strong>Apoptosis</strong> (programmed cell death) and ignore <strong>Density-Dependent Inhibition</strong> and <strong>Anchorage Dependence</strong>.
            </ImportantNote>
            <div className="grid md:grid-cols-2 gap-4 mt-4">
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <h4 className="font-bold text-red-900">Proto-oncogenes</h4>
                <p className="text-sm text-slate-700">Stimulate growth. Mutation makes them <strong>Oncogenes</strong> (Gas pedal stuck).</p>
              </div>
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <h4 className="font-bold text-red-900">Tumor Suppressors</h4>
                <p className="text-sm text-slate-700">Inhibit division (e.g., <strong>p53</strong>). Mutation breaks the brakes.</p>
              </div>
            </div>
            <p className="text-sm text-slate-600 mt-4"><strong>Metastasis:</strong> The spread of cancer cells to other parts of the body.</p>
          </section>
        </div>
      )
    }
  ]
};

export default biologyUnit4Notes;